import { useId } from 'react';

import { cn } from '@/lib/utils';

/**
 * Los dos destellos del ícono, en su lienzo de 48, y el `viewBox` que los
 * encuadra solos. Los reusa la novedad de la barra móvil: el mismo destello
 * dice «nuevo» en toda la app.
 */
export const HERU_GLINTS = {
  viewBox: '27 5 20 20',
  large: 'M33.5 6C33.8 9.6 34.1 10.56 34.1 11.4C34.94 11.4 35.9 11.7 39.5 12C35.9 12.3 34.94 12.6 34.1 12.6C34.1 13.44 33.8 14.4 33.5 18C33.2 14.4 32.9 13.44 32.9 12.6C32.06 12.6 31.1 12.3 27.5 12C31.1 11.7 32.06 11.4 32.9 11.4C32.9 10.56 33.2 9.6 33.5 6Z',
  small: 'M42 15.3C42.21 17.82 42.42 18.49 42.42 19.08C43.01 19.08 43.68 19.29 46.2 19.5C43.68 19.71 43.01 19.92 42.42 19.92C42.42 20.51 42.21 21.18 42 23.7C41.79 21.18 41.58 20.51 41.58 19.92C40.99 19.92 40.32 19.71 37.8 19.5C40.32 19.29 40.99 19.08 41.58 19.08C41.58 18.49 41.79 17.82 42 15.3Z',
} as const;

interface AssistantIconProps {
  size?: number;
  /** Las estrellas chicas destellan. Apagado, se quedan fijas. */
  animated?: boolean;
  className?: string;
}

/**
 * Ícono del asistente: la estrella de heru con dos destellos. Los colores son
 * los tokens `h-primary-60/40/50`, no hex sueltos. El id del degradado sale de
 * `useId` porque el ícono aparece varias veces en pantalla, y dos `<defs>` con
 * el mismo id hacen que uno pinte con el degradado del otro, o sin él.
 */
export const AssistantIcon = ({ size = 16, animated = true, className }: AssistantIconProps) => {
  const gradientId = `heru-star-${useId().replace(/[^a-zA-Z0-9-_]/g, '')}`;
  const glint = cn('fill-h-primary-50', animated && 'heru-assistant-glint');

  return (
    <svg
      viewBox='0 0 48 48'
      width={size}
      height={size}
      fill='none'
      aria-hidden
      className={cn('flex-none', className)}>
      <defs>
        <linearGradient id={gradientId} x1='8' y1='9' x2='28' y2='41' gradientUnits='userSpaceOnUse'>
          <stop offset='0' style={{ stopColor: 'var(--color-h-primary-60)' }} />
          <stop offset='1' style={{ stopColor: 'var(--color-h-primary-40)' }} />
        </linearGradient>
      </defs>
      <path
        fill={`url(#${gradientId})`}
        d='M19 9C20 20 24 24 35 25C24 26 20 30 19 41C18 30 14 26 3 25C14 24 18 20 19 9Z'
      />
      <path
        className={glint}
        d={HERU_GLINTS.large}
      />
      <path
        className={cn(glint, animated && 'heru-assistant-glint-late')}
        d={HERU_GLINTS.small}
      />
    </svg>
  );
};
